import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, CheckCheck, X } from "lucide-react";
import { useNotifications } from "@/hooks/useRealtime";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function NotificationBell({ enabled = true }) {
  const { items, unread, markRead, markAllRead } = useNotifications(enabled);
  const [open, setOpen] = useState(false);

  const iconFor = (kind = "") => {
    if (kind === "sms_inbound") return "💬";
    if (kind.startsWith("meeting")) return "📅";
    if (kind.startsWith("schedule")) return "🗓️";
    if (kind === "user_registered") return "🧑";
    if (kind === "status_change") return "🔔";
    return "✨";
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative rounded-full w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition"
          data-testid="notification-bell"
          aria-label="Notifications"
        >
          <Bell size={18} className="text-slate-700" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse"
              data-testid="notification-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-96 max-h-[70vh] overflow-hidden p-0 rounded-2xl shadow-2xl border-slate-200"
        data-testid="notification-panel"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50 to-white">
          <div>
            <p className="font-display font-black text-slate-900 text-lg leading-tight">Notifications</p>
            <p className="text-xs text-slate-500 mt-0.5">{unread ? `${unread} unread` : "All caught up"}</p>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs" data-testid="notification-mark-all-read">
              <CheckCheck size={14} className="mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center text-slate-400" data-testid="notification-empty">
              <Bell size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No notifications yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-50">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`p-4 flex items-start gap-3 transition ${n.is_read ? "" : "bg-amber-50/40"}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-lg shrink-0">
                    <span>{iconFor(n.kind)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={n.link || "#"}
                      onClick={() => { setOpen(false); if (!n.is_read) markRead([n.id]); }}
                      className="block"
                    >
                      <p className={`text-sm ${n.is_read ? "text-slate-700" : "text-slate-900 font-semibold"} leading-snug`}>{n.title}</p>
                      {n.body && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body}</p>}
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">{timeago(n.created_at)}</p>
                    </Link>
                  </div>
                  {!n.is_read && (
                    <button
                      onClick={() => markRead([n.id])}
                      className="text-slate-400 hover:text-emerald-500 transition"
                      aria-label="Mark read"
                      data-testid={`notification-read-${n.id}`}
                    >
                      <Check size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function timeago(iso) {
  if (!iso) return "";
  const dt = new Date(iso).getTime();
  const s = Math.floor((Date.now() - dt) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
